import * as React from "react";
import { cn } from "../lib/cn";

const SIZE_MAP = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 48,
} as const;

export type AndoLogoSize = keyof typeof SIZE_MAP | number;

export interface AndoLogoProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "size" | "src"> {
  /** Size token (`xs`–`xl`) or a raw pixel number. Default `md` (24px). */
  size?: AndoLogoSize;
}

/**
 * AndoLogo — canonical Ando brand mark (icon-only).
 *
 * Renders the **real** ``/ando-logo-only-icon.svg`` asset (the same
 * vector the V1 marketing site, V2 marketing site, password pages,
 * favicons, and OrbitalBrainCanvas all reference). Each app keeps a
 * copy of the asset in its ``public/`` directory — Vite (SPA) and
 * Next.js (BFF) both resolve ``/ando-logo-only-icon.svg`` against the
 * origin root.
 *
 * Why ``<img>`` instead of an inline SVG path:
 *   - the canonical SVG carries 40+ path nodes + brand gradient stops;
 *     duplicating them inline would dupe the source-of-truth and ship
 *     ~26 KiB of duplicate markup in the bundle on every render.
 *   - browsers cache ``/ando-logo-only-icon.svg`` once and reuse it
 *     across every consumer (sidebar pills, AI popovers, login, …).
 *   - asset can be re-rendered/refreshed by replacing the file in
 *     ``public/`` without rebuilding the component library.
 *
 * Historical (Jun 3, 2026): a simplified inline-path version lived here
 * to shave bytes, but it rendered a 7-node silhouette that diverged
 * from the actual brand mark on every authenticated surface. Reverted
 * to the V1 SVG to keep cross-surface brand parity.
 */
export const AndoLogo = React.forwardRef<HTMLImageElement, AndoLogoProps>(
  ({ size = "md", className, alt = "Ando", ...rest }, ref) => {
    const px = typeof size === "number" ? size : SIZE_MAP[size];
    return (
      <img
        ref={ref}
        src="/ando-logo-only-icon.svg"
        alt={alt}
        width={px}
        height={px}
        draggable={false}
        className={cn("inline-block shrink-0 select-none", className)}
        {...rest}
      />
    );
  },
);
AndoLogo.displayName = "AndoLogo";
